import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaLayerGroup, FaPen } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DecimalMonetario from '../decimal';
import { TipoFormulario } from '../../services/constantes';
import { formatarParaReais } from '../../services/utilitarios';
import { TipoPeriodicidadePagamento } from '../../services/tipos';

interface ParcelaItem {
    num: number;
    data: string;
    valor: number;
    editado: boolean;
    naoContabilizarJuros: boolean;
    naoContabilizarMultas: boolean;
}

interface ModalParcelamentoProps {
    show: boolean;
    onClose: () => void;
    pagamento: {
        id?: string;
        idLocacao: string;
        idEmpresa: string;
        valor: number;
        descricao: string;
        dataPrevista: string;
        asaasPaymentId?: string;
        periodicidadeLocacao?: TipoPeriodicidadePagamento;
    } | null;
    onConfirm: (dados: {
        parcelas: ParcelaItem[];
        descricao: string;
        excluirOriginal: boolean;
    }) => Promise<void>;
    integradoAsaas?: boolean;
}

const PERIODICIDADES = [
    { value: 'semanal', label: 'Semanal', dias: 7, tipo: TipoPeriodicidadePagamento.Semanal },
    { value: 'quinzenal', label: 'Quinzenal', dias: 15, tipo: TipoPeriodicidadePagamento.Quinzenal },
    { value: 'mensal', label: 'Mensal', dias: 30, tipo: TipoPeriodicidadePagamento.Mensal },
];

function periodicidadeFromLocacao(tipo?: TipoPeriodicidadePagamento): 'semanal' | 'quinzenal' | 'mensal' {
    switch (tipo) {
        case TipoPeriodicidadePagamento.Semanal: return 'semanal';
        case TipoPeriodicidadePagamento.Quinzenal: return 'quinzenal';
        default: return 'mensal';
    }
}

function extrairDataISO(d: string): string {
    if (!d) return new Date().toISOString().split('T')[0];
    return d.split('T')[0];
}

function calcularDataParcela(baseStr: string, index: number, periodicidade: string): string {
    const base = new Date(baseStr + 'T12:00:00');
    const cfg = PERIODICIDADES.find(p => p.value === periodicidade);
    const data = new Date(base);
    if (periodicidade === 'mensal') data.setMonth(data.getMonth() + index);
    else data.setDate(data.getDate() + (cfg?.dias || 30) * index);
    return data.toISOString().split('T')[0];
}

const ModalParcelamento: React.FC<ModalParcelamentoProps> = ({ show, onClose, pagamento, onConfirm, integradoAsaas = false }) => {
    const [qtdParcelas, setQtdParcelas] = useState(2);
    const [periodicidade, setPeriodicidade] = useState<'semanal' | 'quinzenal' | 'mensal'>('mensal');
    const [dataInicio, setDataInicio] = useState('');
    const [valorTotal, setValorTotal] = useState(0);
    const [descricao, setDescricao] = useState('');
    const [parcelas, setParcelas] = useState<ParcelaItem[]>([]);
    const [jurosGlobal, setJurosGlobal] = useState(false);
    const [multaGlobal, setMultaGlobal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [parcelaEditando, setParcelaEditando] = useState<number | null>(null);

    const gerarParcelas = useCallback((qtd: number, total: number, dataBase: string, period: string, juros: boolean, multa: boolean, atuais: ParcelaItem[]) => {
        if (qtd <= 0 || !dataBase) return [];
        const vBase = Math.round((total / qtd) * 100) / 100;
        const items: ParcelaItem[] = [];
        for (let i = 0; i < qtd; i++) {
            const ex = atuais.find(p => p.num === i + 1 && p.editado);
            items.push({
                num: i + 1,
                data: calcularDataParcela(dataBase, i, period),
                valor: ex ? ex.valor : (i === qtd - 1 ? Math.round((total - vBase * (qtd - 1)) * 100) / 100 : vBase),
                editado: ex?.editado || false,
                naoContabilizarJuros: ex ? ex.naoContabilizarJuros : juros,
                naoContabilizarMultas: ex ? ex.naoContabilizarMultas : multa,
            });
        }
        return items;
    }, []);

    useEffect(() => {
        if (show && pagamento) {
            const per = periodicidadeFromLocacao(pagamento.periodicidadeLocacao);
            const dt = extrairDataISO(pagamento.dataPrevista);
            setQtdParcelas(2); setPeriodicidade(per); setDataInicio(dt);
            setValorTotal(pagamento.valor); setDescricao(pagamento.descricao || 'ALUGUEL');
            setJurosGlobal(false); setMultaGlobal(false); setIsSubmitting(false); setParcelaEditando(null);
            setParcelas(gerarParcelas(2, pagamento.valor, dt, per, false, false, []));
        }
    }, [show, pagamento, gerarParcelas]);

    useEffect(() => {
        if (show && dataInicio)
            setParcelas(prev => gerarParcelas(qtdParcelas, valorTotal, dataInicio, periodicidade, jurosGlobal, multaGlobal, prev));
    }, [qtdParcelas, periodicidade, dataInicio, valorTotal, show, gerarParcelas]);

    const handleJurosGlobal = (c: boolean) => { setJurosGlobal(c); setParcelas(prev => prev.map(p => ({ ...p, naoContabilizarJuros: c }))); };
    const handleMultaGlobal = (c: boolean) => { setMultaGlobal(c); setParcelas(prev => prev.map(p => ({ ...p, naoContabilizarMultas: c }))); };

    const handleValorChange = (num: number, v: number) => setParcelas(prev => prev.map(p => p.num === num ? { ...p, valor: v, editado: true } : p));
    const handleReset = (num: number) => {
        const vBase = Math.round((valorTotal / qtdParcelas) * 100) / 100;
        const vFinal = num === qtdParcelas ? Math.round((valorTotal - vBase * (qtdParcelas - 1)) * 100) / 100 : vBase;
        setParcelas(prev => prev.map(p => p.num === num ? { ...p, valor: vFinal, editado: false } : p));
        setParcelaEditando(null);
    };

    const soma = parcelas.reduce((a, p) => a + p.valor, 0);
    const diff = Math.round((soma - valorTotal) * 100) / 100;

    const handleConfirmar = async () => {
        if (qtdParcelas < 2 || qtdParcelas > 60) { toast.warning('Informe entre 2 e 60 parcelas.'); return; }
        if (!dataInicio) { toast.warning('Informe a data do primeiro vencimento.'); return; }
        if (valorTotal <= 0) { toast.warning('O valor total deve ser maior que zero.'); return; }
        setIsSubmitting(true);
        try { await onConfirm({ parcelas, descricao, excluirOriginal: true }); onClose(); }
        catch { } finally { setIsSubmitting(false); }
    };

    if (!pagamento) return null;

    const lbl: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px' };

    return (
        <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
            <Modal.Header style={{ backgroundColor: '#1f467e' }} className="text-white border-0">
                <Modal.Title style={{ fontSize: '16px', fontWeight: 600 }} className="d-flex align-items-center gap-2">
                    <FaLayerGroup /> Parcelar Pagamento
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-0" style={{ maxHeight: '78vh', overflowY: 'auto' }}>
                {/* Resumo */}
                <div style={{ background: '#f8f9fb', borderBottom: '1px solid #eee', padding: '14px 24px' }}>
                    <Row className="align-items-center">
                        <Col xs="auto">
                            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pagamento original</div>
                            <div style={{ fontSize: '22px', fontWeight: 700, color: '#1f467e' }}>{formatarParaReais(pagamento.valor)}</div>
                        </Col>
                        <Col>
                            <div style={{ fontSize: '13px', color: '#555' }}>
                                Vencimento: <strong>{pagamento.dataPrevista?.split('T')[0]?.split('-').reverse().join('/')}</strong>
                                {pagamento.descricao && <> &mdash; {pagamento.descricao}</>}
                            </div>
                            {integradoAsaas && <span style={{ background: '#4CAF50', color: '#fff', borderRadius: 4, padding: '2px 10px', fontSize: '11px', fontWeight: 600, marginTop: 4, display: 'inline-block' }}>Integrado ao Asaas</span>}
                        </Col>
                    </Row>
                </div>

                <div style={{ padding: '24px' }}>
                    {/* Configuracoes */}
                    <Row className="g-3">
                        <Col xs={12} md={4}>
                            <div style={lbl}>Valor total a parcelar</div>
                            <DecimalMonetario
                                campo={{ nome: "valorTotal", tipo: "monetario", required: true, modo: TipoFormulario.tpEdicao }}
                                getFieldValue={() => valorTotal}
                                handleInputChange={(_: any, v: number) => setValorTotal(v)}
                                largura="100%"
                                valorInicial={pagamento.valor}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <div style={lbl}>Qtd. parcelas</div>
                            <Form.Control type="number" min={2} max={60} value={qtdParcelas}
                                onChange={(e) => setQtdParcelas(Math.max(2, parseInt(e.target.value) || 2))} />
                        </Col>
                        <Col xs={6} md={3}>
                            <div style={lbl}>Periodicidade</div>
                            <Form.Control as="select" value={periodicidade} onChange={(e: any) => setPeriodicidade(e.target.value)}>
                                {PERIODICIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </Form.Control>
                        </Col>
                        <Col xs={12} md={3}>
                            <div style={lbl}>1º vencimento</div>
                            <Form.Control type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
                        </Col>
                    </Row>

                    <Row className="g-3 mt-0">
                        <Col xs={12}>
                            <div style={lbl}>Descrição</div>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={descricao}
                                onChange={(e) => setDescricao(e.target.value.toUpperCase())}
                                placeholder="Ex: ALUGUEL DO VEÍCULO"
                            />
                        </Col>
                    </Row>

                    <Row className="g-3 mt-1">
                        <Col xs={12} md={6}>
                            <Form.Check
                                type="checkbox"
                                id="jurosGlobal"
                                checked={jurosGlobal}
                                onChange={(e) => handleJurosGlobal(e.target.checked)}
                                label="Não cobrar juros em nenhuma parcela"
                            />
                        </Col>

                        <Col xs={12} md={6}>
                            <Form.Check
                                type="checkbox"
                                id="multaGlobal"
                                checked={multaGlobal}
                                onChange={(e) => handleMultaGlobal(e.target.checked)}
                                label="Não cobrar multa em nenhuma parcela"
                            />
                        </Col>
                    </Row>

                    {/* Divisor */}
                    <hr style={{ margin: '20px 0 16px', borderColor: '#e5e7eb' }} />

                    {/* Parcelas */}
                    {parcelas.length > 0 && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <span style={{ fontSize: '14px', fontWeight: 700, color: '#1f467e' }}>
                                    Previa das {qtdParcelas} parcelas
                                </span>
                                {Math.abs(diff) > 0.01 && (
                                    <span style={{ fontSize: '12px', background: '#fef3c7', color: '#92400e', padding: '2px 10px', borderRadius: 4, fontWeight: 600 }}>
                                        Soma difere em {formatarParaReais(Math.abs(diff))}
                                    </span>
                                )}
                            </div>

                            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                                {/* Cabecalho */}
                                <div style={{
                                    display: 'flex', padding: '10px 16px', background: '#f3f4f6',
                                    borderBottom: '1px solid #e5e7eb', fontSize: '11px', fontWeight: 700,
                                    color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px',
                                }}>
                                    <span style={{ width: 55 }}>Parcela</span>
                                    <span style={{ width: 100 }}>Vencimento</span>
                                    <span style={{ flex: 1, textAlign: 'right', paddingRight: 12 }}>Valor</span>
                                    <span style={{ width: 100, textAlign: 'center' }}>Sem Juros</span>
                                    <span style={{ width: 100, textAlign: 'center' }}>Sem Multa</span>
                                </div>

                                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                                    {parcelas.map((p, idx) => (
                                        <div key={p.num} style={{
                                            display: 'flex', alignItems: 'center', padding: '0 16px',
                                            minHeight: 48,
                                            borderBottom: idx < parcelas.length - 1 ? '1px solid #f3f4f6' : 'none',
                                            background: p.editado ? '#fffbeb' : '#fff',
                                        }}>
                                            {/* Numero */}
                                            <span style={{ width: 55 }}>
                                                <span style={{
                                                    background: '#1f467e', color: '#fff', borderRadius: 4,
                                                    padding: '2px 6px', fontSize: '11px', fontWeight: 700,
                                                }}>{p.num}/{qtdParcelas}</span>
                                            </span>

                                            {/* Data */}
                                            <span style={{ width: 100, fontSize: '13px', color: '#374151' }}>
                                                {p.data.split('-').reverse().join('/')}
                                            </span>

                                            {/* Valor */}
                                            <div style={{ flex: 1, textAlign: 'right', paddingRight: 12 }}>
                                                {parcelaEditando === p.num ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, padding: '4px 0' }}>
                                                        <span style={{ color: '#888', fontSize: '13px' }}>R$</span>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            autoFocus
                                                            defaultValue={p.valor.toFixed(2)}
                                                            onBlur={(e) => {
                                                                const v = parseFloat(e.target.value) || 0;
                                                                handleValorChange(p.num, Math.round(v * 100) / 100);
                                                                setParcelaEditando(null);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                                                if (e.key === 'Escape') { setParcelaEditando(null); }
                                                            }}
                                                            style={{
                                                                width: 110, padding: '4px 8px', fontSize: '14px',
                                                                border: '2px solid #1f467e', borderRadius: 4,
                                                                textAlign: 'right', outline: 'none',
                                                            }}
                                                        />
                                                        {p.editado && (
                                                            <button type="button" onClick={() => handleReset(p.num)}
                                                                style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 4, padding: '3px 8px', fontSize: '11px', cursor: 'pointer', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                                                Desfazer
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span
                                                        onClick={() => setParcelaEditando(p.num)}
                                                        style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 6,
                                                            cursor: 'pointer', padding: '4px 8px', borderRadius: 4,
                                                        }}
                                                        title="Clique para editar o valor"
                                                    >
                                                        <span style={{ fontSize: '14px', fontWeight: p.editado ? 700 : 500, color: p.editado ? '#b45309' : '#111' }}>
                                                            {formatarParaReais(p.valor)}
                                                        </span>
                                                        <FaPen style={{ fontSize: '10px', color: '#bbb' }} />
                                                    </span>
                                                )}
                                            </div>

                                            {/* Sem Juros */}
                                            <div style={{ width: 100, textAlign: 'center' }}>
                                                <Form.Check type="checkbox" checked={p.naoContabilizarJuros}
                                                    onChange={(e) => setParcelas(prev => prev.map(x => x.num === p.num ? { ...x, naoContabilizarJuros: e.target.checked } : x))} />
                                            </div>

                                            {/* Sem Multa */}
                                            <div style={{ width: 100, textAlign: 'center' }}>
                                                <Form.Check type="checkbox" checked={p.naoContabilizarMultas}
                                                    onChange={(e) => setParcelas(prev => prev.map(x => x.num === p.num ? { ...x, naoContabilizarMultas: e.target.checked } : x))} />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Rodape total */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', padding: '14px 16px',
                                    background: '#f0f4ff', borderTop: '2px solid #d6e0f0',
                                }}>
                                    <span style={{ width: 55 }}></span>
                                    <span style={{ width: 100, fontWeight: 700, color: '#1f467e', fontSize: '14px' }}>Total</span>
                                    <span style={{
                                        flex: 1, textAlign: 'right', paddingRight: 12,
                                        fontWeight: 700, fontSize: '18px',
                                        color: Math.abs(diff) > 0.01 ? '#dc3545' : '#1f467e',
                                    }}>
                                        {formatarParaReais(soma)}
                                    </span>
                                    <span style={{ width: 200 }}></span>
                                </div>
                            </div>
                        </>
                    )}

                    {integradoAsaas && (
                        <Alert variant="info" className="py-2 mt-3 mb-0" style={{ fontSize: '12px' }}>
                            <strong>Asaas:</strong> As parcelas serão criadas como cobranças no Asaas e o pagamento original será removido.
                        </Alert>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between" style={{ borderTop: '1px solid #e5e7eb' }}>
                <Button variant="outline-secondary" onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
                <Button
                    onClick={handleConfirmar}
                    disabled={isSubmitting}
                    style={{ backgroundColor: '#1f467e', borderColor: '#1f467e', fontWeight: 600, padding: '8px 28px' }}
                    className="d-flex align-items-center gap-2"
                >
                    {isSubmitting
                        ? <><span className="spinner-border spinner-border-sm" role="status" /> Gerando...</>
                        : <><FaLayerGroup /> Gerar {qtdParcelas} Parcelas</>
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalParcelamento;
